import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useWallet } from '../contexts/WalletContext'
import { useSettlementResult, useClaim, parseClaimError } from '../hooks/useSettlementResult'
import type { WinnerInfo } from '../hooks/useSettlementResult'
import { CHALLENGE_TYPE, formatWeiToEth, formatAddress } from '../types/challenge'

const SEPOLIA_ETHERSCAN = 'https://sepolia.etherscan.io'

function WinnerRow({
  winner,
  onClaim,
  isPending = false,
  showClaimBtn = false,
}: {
  winner: WinnerInfo
  onClaim?: () => void
  isPending?: boolean
  showClaimBtn?: boolean
}) {
  return (
    <div
      data-testid={`winner-item-${winner.address}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '11px 14px',
        borderRadius: 'var(--radius)',
        marginBottom: 6,
        background: winner.isCurrentUser ? 'var(--primary-weak)' : 'var(--surface-2)',
        border: `1px solid ${winner.isCurrentUser ? 'var(--primary-line)' : 'transparent'}`,
        flexWrap: 'wrap',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          className="mono"
          style={{
            fontSize: 13,
            fontWeight: winner.isCurrentUser ? 700 : 400,
            color: winner.isCurrentUser ? 'var(--primary-strong)' : 'var(--ink)',
          }}
        >
          {formatAddress(winner.address)}
        </span>
        {winner.isCurrentUser && (
          <span className="badge badge-primary" style={{ fontSize: 11 }}>
            내 지갑
          </span>
        )}
      </div>
      <div>
        {winner.isClaimed ? (
          <span className="badge badge-success" data-testid={`claimed-badge-${winner.address}`}>
            수령 완료 ✓
          </span>
        ) : showClaimBtn ? (
          <button
            className="btn btn-primary btn-sm"
            onClick={onClaim}
            disabled={isPending}
            data-testid={`claim-btn-${winner.address}`}
          >
            {isPending ? '처리 중…' : '정산 받기'}
          </button>
        ) : null}
      </div>
    </div>
  )
}

export default function SettlementResult() {
  const { address } = useParams<{ address: string }>()
  const { address: userAddress } = useWallet()
  const [claimError, setClaimError] = useState<string | null>(null)

  const { data, isLoading } = useSettlementResult(address ?? '', userAddress)
  const { mutate: claim, isPending: isClaimPending } = useClaim(address ?? '')

  function handleClaim() {
    setClaimError(null)
    claim(undefined, { onError: (err) => setClaimError(parseClaimError(err)) })
  }

  if (!address)
    return (
      <div className="empty">
        <p>잘못된 챌린지 주소입니다.</p>
      </div>
    )
  if (isLoading)
    return (
      <div className="empty" data-testid="settlement-loading">
        <div className="spinner spinner-lg" />
        <p>정산 결과를 불러오는 중…</p>
      </div>
    )
  if (!data)
    return (
      <div className="empty">
        <p>데이터를 불러올 수 없습니다.</p>
      </div>
    )

  const { challenge, payoutPerWinner, totalPool, winners, finalizeTxHash } = data
  const isPromoToken = challenge.challengeType === CHALLENGE_TYPE.PROMO_TOKEN
  const isPromoPhysical = challenge.challengeType === CHALLENGE_TYPE.PROMO_PHYSICAL
  const isPromo = isPromoToken || isPromoPhysical
  const isEntryFee = challenge.challengeType === CHALLENGE_TYPE.ENTRY_FEE
  const fee = (totalPool * 3n) / 100n

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <Link
        to={`/challenge/${address}`}
        className="btn btn-quiet btn-sm"
        style={{ marginBottom: 16, paddingLeft: 0 }}
      >
        ← 뒤로
      </Link>

      {/* Header */}
      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 4px' }} data-testid="result-title">
          {isPromo ? `🎉 ${challenge.name} — 추첨 결과` : `${challenge.name} — 정산 결과`}
        </h1>
        <p style={{ color: 'var(--ink-3)', margin: 0, fontSize: 13 }}>
          종료: {new Date(challenge.endTime * 1000).toLocaleDateString('ko-KR')}
        </p>

        {isEntryFee && (
          <div className="statrow" style={{ marginTop: 16 }}>
            <div className="stat">
              <div className="lbl">전체 풀</div>
              <div className="val eth-amt" data-testid="pool-amount">
                {formatWeiToEth(totalPool)} ETH
              </div>
            </div>
            <div className="stat">
              <div className="lbl">수수료 (3%)</div>
              <div className="val eth-amt" data-testid="fee-amount">
                {formatWeiToEth(fee)} ETH
              </div>
            </div>
            <div className="stat">
              <div className="lbl">완주자</div>
              <div className="val" data-testid="winner-count">
                {winners.length}명
              </div>
            </div>
            <div className="stat">
              <div className="lbl">1인당 지급</div>
              <div className="val eth-amt" data-testid="payout-amount">
                {winners.length > 0 ? `${formatWeiToEth(payoutPerWinner)} ETH` : '-'}
              </div>
            </div>
          </div>
        )}
        {isPromo && (
          <div style={{ marginTop: 14, fontSize: 14 }}>
            {isPromoToken && totalPool > 0n && (
              <div style={{ marginBottom: 6 }}>
                <span style={{ color: 'var(--ink-3)' }}>경품:</span>{' '}
                <span className="eth-amt" style={{ fontWeight: 700 }}>
                  {formatWeiToEth(payoutPerWinner * BigInt(winners.length))} ETH
                </span>
              </div>
            )}
            {isPromoPhysical && (
              <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 6 }}>
                실물 경품 — 개설자가 별도로 연락드립니다.
              </div>
            )}
            {finalizeTxHash && (
              <div style={{ fontSize: 13 }}>
                <span style={{ color: 'var(--ink-3)', marginRight: 6 }}>VRF 트랜잭션:</span>
                <a
                  href={`${SEPOLIA_ETHERSCAN}/tx/${finalizeTxHash}`}
                  target="_blank"
                  rel="noreferrer"
                  className="mono"
                  style={{ color: 'var(--primary)', fontSize: 12 }}
                  data-testid="vrf-tx-link"
                >
                  {formatAddress(finalizeTxHash)} ↗
                </a>
              </div>
            )}
          </div>
        )}
        {isEntryFee && finalizeTxHash && (
          <div style={{ marginTop: 10, fontSize: 13 }}>
            <span style={{ color: 'var(--ink-3)', marginRight: 6 }}>정산 트랜잭션:</span>
            <a
              href={`${SEPOLIA_ETHERSCAN}/tx/${finalizeTxHash}`}
              target="_blank"
              rel="noreferrer"
              className="mono"
              style={{ color: 'var(--primary)', fontSize: 12 }}
              data-testid="finalize-tx-link"
            >
              {formatAddress(finalizeTxHash)} ↗
            </a>
          </div>
        )}
      </div>

      {/* Winners */}
      <div className="card card-pad">
        <h2 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 14px' }}>
          {isPromo ? `당첨자 (${winners.length}명)` : `완주자 목록 (${winners.length}명)`}
        </h2>
        {claimError && (
          <div className="alert alert-danger" role="alert" style={{ marginBottom: 12 }}>
            {claimError}
          </div>
        )}
        {winners.length === 0 ? (
          <div className="empty" data-testid="no-winners">
            <p>{isPromo ? '당첨자가 없습니다.' : '완주자가 없습니다.'}</p>
          </div>
        ) : (
          winners.map((winner) => {
            const canClaim = isEntryFee && winner.isCurrentUser && !winner.isClaimed
            return (
              <WinnerRow
                key={winner.address}
                winner={winner}
                onClaim={canClaim ? handleClaim : undefined}
                isPending={isClaimPending}
                showClaimBtn={canClaim}
              />
            )
          })
        )}
        {isPromoToken && winners.length > 0 && (
          <div className="alert alert-success" style={{ marginTop: 12 }}>
            ETH 경품 자동 지급 완료 ✓
          </div>
        )}
        {isPromoPhysical && winners.length > 0 && (
          <div className="alert alert-warn" style={{ marginTop: 12 }}>
            실물 경품: 개설자가 별도로 연락드립니다.
          </div>
        )}
      </div>
    </div>
  )
}
